const configuredApiUrl = process.env.IKOOS_API_URL || '';

const trimTrailingSlash = value => value.replace(/\/$/, '');

const inferApiBase = () => {
    if (configuredApiUrl) return trimTrailingSlash(configuredApiUrl);
    if (typeof window === 'undefined') return 'http://localhost:8602';

    if (window.location.protocol === 'file:') return 'http://localhost:8602';

    return '';
};

const API_BASE = inferApiBase();

const parseJsonResponse = async response => {
    const text = await response.text();
    const payload = text ? JSON.parse(text) : {};

    if (!response.ok) {
        const error = new Error(payload.error || 'Request gagal.');
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    return payload;
};

const requestJson = (path, options = {}) => {
    const headers = {
        Accept: 'application/json',
        ...(options.headers || {})
    };

    if (options.body && typeof options.body === 'string') {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    return fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        ...options,
        headers
    }).then(parseJsonResponse);
};

const buildProjectQuery = params => {
    const query = new URLSearchParams();
    if (Object.prototype.hasOwnProperty.call(params, 'originalId')) query.set('original_id', params.originalId);
    if (Object.prototype.hasOwnProperty.call(params, 'isCopy')) query.set('is_copy', params.isCopy);
    if (Object.prototype.hasOwnProperty.call(params, 'isRemix')) query.set('is_remix', params.isRemix);
    if (Object.prototype.hasOwnProperty.call(params, 'title')) query.set('title', params.title);
    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
};

const absoluteUrl = url => {
    if (!url) return null;
    if (/^https?:\/\//.test(url)) return url;
    return `${API_BASE}${url}`;
};

const normalizeUser = user => {
    if (!user) return null;
    return {
        ...user,
        avatarUrl: absoluteUrl(user.avatarUrl)
    };
};

export default {
    apiBase: API_BASE,
    projectHost: `${API_BASE}/api/projects`,
    assetHost: `${API_BASE}/api/assets`,

    me () {
        return requestJson('/api/auth/me').then(payload => ({
            ...payload,
            user: normalizeUser(payload.user)
        }));
    },

    register ({username, email, password}) {
        return requestJson('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify({username, email, password})
        }).then(payload => ({
            ...payload,
            user: normalizeUser(payload.user)
        }));
    },

    login ({identity, password}) {
        return requestJson('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({identity, password})
        }).then(payload => ({
            ...payload,
            user: normalizeUser(payload.user)
        }));
    },

    logout () {
        return requestJson('/api/auth/logout', {
            method: 'POST'
        });
    },

    forgotPassword ({email}) {
        return requestJson('/api/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({email})
        });
    },

    resetPassword ({token, password}) {
        return requestJson('/api/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({token, password})
        });
    },

    updateProfile ({displayName, username, email}) {
        return requestJson('/api/auth/profile', {
            method: 'PUT',
            body: JSON.stringify({displayName, username, email})
        }).then(payload => ({
            ...payload,
            user: normalizeUser(payload.user)
        }));
    },

    changePassword ({currentPassword, password}) {
        return requestJson('/api/auth/password', {
            method: 'PUT',
            body: JSON.stringify({currentPassword, password})
        });
    },

    uploadAvatar ({avatarDataUrl}) {
        return requestJson('/api/auth/avatar', {
            method: 'POST',
            body: JSON.stringify({avatarDataUrl})
        }).then(payload => ({
            ...payload,
            user: normalizeUser(payload.user)
        }));
    },

    listProjects () {
        return requestJson('/api/projects').then(payload => ({
            projects: (payload.projects || []).map(project => ({
                ...project,
                thumbnailUrl: absoluteUrl(project.thumbnailUrl)
            }))
        }));
    },

    saveProject (projectId, vmState, params = {}) {
        const creatingProject = projectId === null || typeof projectId === 'undefined' || projectId === '0';
        const query = buildProjectQuery(params);
        const path = creatingProject ? `/api/projects/${query}` : `/api/projects/${projectId}${query}`;

        return requestJson(path, {
            method: creatingProject ? 'POST' : 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: vmState
        });
    },

    deleteProject (projectId) {
        return requestJson(`/api/projects/${projectId}`, {
            method: 'DELETE'
        });
    },

    shareProject (projectId) {
        return requestJson(`/api/projects/${projectId}/share`, {
            method: 'POST'
        });
    },

    saveProjectThumbnail (projectId, thumbnail) {
        return fetch(`${API_BASE}/api/projects/${projectId}/thumbnail`, {
            method: 'POST',
            credentials: 'include',
            body: thumbnail
        }).then(parseJsonResponse);
    }
};
